import type {
  Stripe,
  StripeElements,
  StripeCardElement,
} from '@stripe/stripe-js';
import { translatePaymentError } from './api/payment-errors';

export function useStripe() {
  const nuxtApp = useNuxtApp();

  const isLoading = ref(false);
  const isCardComplete = ref(false);
  const cardError = ref<string | null>(null);

  let stripe: Stripe | null = null;
  let elements: StripeElements | null = null;
  let cardElement: StripeCardElement | null = null;

  async function mountCardElement(container: HTMLElement) {
    stripe = await nuxtApp.$stripe();
    if (!stripe) {
      cardError.value =
        'Payment system is not available. Please try again later.';
      return;
    }

    elements = stripe.elements();
    cardElement = elements.create('card', {
      style: {
        base: {
          color: '#40484C',
          fontFamily: '"Roboto", sans-serif',
          fontSize: '16px',
          '::placeholder': { color: '#90A4AE' },
        },
        invalid: {
          color: '#BA1A1A',
          iconColor: '#BA1A1A',
        },
      },
    });

    cardElement.on('change', (event) => {
      isCardComplete.value = event.complete;
      cardError.value = event.error ? event.error.message : null;
    });

    cardElement.mount(container);
  }

  async function confirmSetupIntent(clientSecret: string): Promise<string> {
    if (!stripe || !cardElement) {
      throw new Error('Card element is not mounted');
    }

    isLoading.value = true;
    cardError.value = null;

    try {
      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card: cardElement },
      });

      if (result.error) {
        const message = result.error.code
          ? translatePaymentError(result.error.code)
          : result.error.message ||
            'A payment error occurred. Please try again.';
        cardError.value = message;
        throw new Error(message);
      }

      return result.setupIntent!.payment_method as string;
    } finally {
      isLoading.value = false;
    }
  }

  function destroyCardElement() {
    if (cardElement) {
      cardElement.unmount();
      cardElement.destroy();
      cardElement = null;
    }
    elements = null;
    stripe = null;
    isCardComplete.value = false;
    cardError.value = null;
  }

  return {
    isLoading,
    isCardComplete,
    cardError,
    mountCardElement,
    confirmSetupIntent,
    destroyCardElement,
  };
}
